import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetMeQuery, useGetUserQuery } from '../shared/services/meApi';
import { useActipayTracking } from '../shared/hook/useActipayTracking';
import { useKeycloak } from '@react-keycloak/web';
import { TrackingEvents } from '../shared/enums';
import { daysSinceDate } from '../shared/utils';

export const RoutesTracking: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname + location.search;
  const { keycloak } = useKeycloak();
  // Get user data.
  const { data: me, isLoading: isLoadingMe } = useGetMeQuery();
  const { data: userInfo, isLoading: isLoadingUser } = useGetUserQuery(
    { userId: me?.uuid || '' },
    { skip: !me },
  );
  // Init tracking hook.
  const { actipayTrackEvent, isProfileLoading } = useActipayTracking(); // 100% autonome
  // Stores the last tracked path.
  const lastTrackedPath = useRef<string | null>(null);

  // Écouteur de Login sécurisé
  useEffect(() => {
    const triggerLoginTrack = localStorage.getItem('kc_logged_in_trigger');
    const isAlreadyLogged = localStorage.getItem('kc_logged_in');

    // On attend que le profil API soit chargé pour avoir l'identifiant exact avant d'envoyer l'event login
    if (
      triggerLoginTrack === 'true' &&
      isAlreadyLogged !== 'true' &&
      !isProfileLoading
    ) {
      const lastLogin = localStorage.getItem('kc_login_date');
      let daysElapsedSinceLastLogin = 0;
      let daysElapsedSinceSignup = 0;

      if (lastLogin) {
        daysElapsedSinceLastLogin = daysSinceDate(lastLogin);
        daysElapsedSinceSignup = daysSinceDate(
          keycloak.tokenParsed?.created_at.toString(),
        );
        localStorage.removeItem('kyb_start_time');
      }

      actipayTrackEvent(TrackingEvents.login, {
        method: 'email',
        days_since_last_login: daysElapsedSinceLastLogin,
        days_since_signup: daysElapsedSinceSignup,
      });

      localStorage.setItem('kc_logged_in', 'true');
      localStorage.removeItem('kc_logged_in_trigger'); // Nettoyage du trigger
      localStorage.setItem('kc_login_date', Date.now().toString());
    }
  }, [isProfileLoading, actipayTrackEvent, keycloak]);

  useEffect(() => {
    // Wait for API data.
    if (isLoadingMe || isLoadingUser || !me || !userInfo) {
      return;
    }

    // Do not track page_view if it's similar to the last one.
    if (lastTrackedPath.current === currentPath) {
      return;
    }

    // Track data.
    actipayTrackEvent(TrackingEvents.pageView, {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
      page_search: location.search,
    });

    // Lock current path
    lastTrackedPath.current = currentPath;
  }, [
    currentPath,
    me,
    userInfo,
    isLoadingMe,
    isLoadingUser,
    actipayTrackEvent,
    location,
  ]);

  return null;
};
